import { useState } from "react";
import { ThumbsUp, ThumbsDown, Volume2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export const CrowdReportCard = ({ report, onUpdate }) => {
  const [voting, setVoting] = useState(false);

  const handleVote = async (voteType) => {
    setVoting(true);
    try {
      const response = await axios.post(`${API}/crowd-reports/${report.id}/vote`, {
        vote_type: voteType,
      });
      
      toast.success(
        voteType === "upvote"
          ? "Thanks for confirming the noise level!"
          : "Thanks for your feedback!"
      );
      
      if (onUpdate) {
        onUpdate(response.data);
      }
    } catch (e) {
      console.error("Vote error:", e);
      toast.error("Failed to submit vote");
    } finally {
      setVoting(false);
    }
  };

  const getNoiseColor = (level) => {
    if (level >= 8) return { bg: "bg-red-100", text: "text-red-600", fill: "#F87171" };
    if (level >= 5) return { bg: "bg-yellow-100", text: "text-yellow-600", fill: "#FBBF24" };
    return { bg: "bg-green-100", text: "text-green-600", fill: "#34D399" };
  };

  const noiseStyle = getNoiseColor(report.noise_level);

  const getNoiseLabel = (level) => {
    if (level >= 8) return "Very Loud";
    if (level >= 5) return "Moderate";
    return "Quiet";
  };

  return (
    <div
      className="crowd-card"
      data-testid={`crowd-report-${report.id}`}
      role="article"
      aria-label={`${report.section_name} noise report`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3
            className="font-semibold text-base"
            style={{ fontFamily: "Lexend, sans-serif" }}
          >
            {report.section_name}
          </h3>
          
          <div className="flex items-center gap-2 mt-2">
            <Volume2 size={16} className={noiseStyle.text} />
            <span className={`text-sm ${noiseStyle.text}`}>
              {getNoiseLabel(report.noise_level)}
            </span>
          </div>
          
          {/* Noise Level Bar */}
          <div className="mt-3">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${report.noise_level * 10}%`,
                  backgroundColor: noiseStyle.fill,
                }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>Quiet</span>
              <span>Level: {report.noise_level}/10</span>
              <span>Loud</span>
            </div>
          </div>
        </div>
        
        <span
          className={`text-xs font-medium px-3 py-1 rounded-full ${noiseStyle.bg} ${noiseStyle.text}`}
        >
          {report.noise_level}/10
        </span>
      </div>

      {/* Vote Buttons */}
      <div className="flex items-center gap-3 mt-4">
        <p className="text-sm text-muted-foreground flex-1">
          Is this accurate?
        </p>
        
        <button
          className="vote-button"
          onClick={() => handleVote("upvote")}
          disabled={voting}
          aria-label="Confirm noise level is accurate"
          data-testid={`upvote-${report.id}`}
        >
          <ThumbsUp size={16} className="text-green-600" />
          <span>{report.upvotes}</span>
        </button>
        
        <button
          className="vote-button"
          onClick={() => handleVote("downvote")}
          disabled={voting}
          aria-label="Report inaccurate noise level"
          data-testid={`downvote-${report.id}`}
        >
          <ThumbsDown size={16} className="text-red-600" />
          <span>{report.downvotes}</span>
        </button>
      </div>
    </div>
  );
};
